import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core'
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable'
import type { SchemaField } from '@doneverse/data/utils'
import { zodResolver } from '@hookform/resolvers/zod'
import { AnimatePresence, motion } from 'framer-motion'
import { Plus, Loader2, Layers, PanelRight } from 'lucide-react'
import { useState } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import type { FieldErrors } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import styles from './SchemaEditor.module.css'
import { FieldEditor } from '@/components/admin/FieldEditor'
import { SchemaPreview } from '@/components/admin/SchemaPreview'
import { Button } from '@/components/ui/button'
import { Form } from '@/components/ui/form'
import { SectionHeader } from '@/components/ui/section-header'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { useIsMobile } from '@/hooks/use-mobile'

/* ─── Zod Schema ────────────────────────────────────────── */

const schemaFieldSchema = z
  .object({
    id: z.union([z.string(), z.number()]),
    type: z.literal('section').optional(),
    title: z.string().optional(),
    question: z.string().optional(),
    description: z.string().optional(),
    field_type: z.enum(['short_text', 'long_text', 'single_select', 'multi_select', 'rank']).optional(),
    displayAs: z
      .enum(['checkbox', 'radio', 'long_text_xl', 'image_select', 'multi_list', 'searchable_select'])
      .optional(),
    options: z.array(z.string()).optional(),
    optionImages: z.record(z.string(), z.string()).optional(),
    required: z.boolean().optional(),
    showWhen: z
      .object({
        questionId: z.string(),
        equals: z.union([z.string(), z.array(z.string())]).optional(),
        isNotEmpty: z.boolean().optional(),
      })
      .optional(),
  })
  .superRefine((data, ctx) => {
    if (data.type === 'section') {
      if (!data.title?.trim()) {
        ctx.addIssue({ code: 'custom', message: 'Section title is required', path: ['title'] })
      }
    } else {
      if (!data.question?.trim()) {
        ctx.addIssue({ code: 'custom', message: 'Question text is required', path: ['question'] })
      }
      const effectiveDisplay = data.displayAs || data.field_type || ''
      const needsOptions = [
        'single_select',
        'searchable_select',
        'multi_select',
        'rank',
        'radio',
        'image_select',
      ].includes(effectiveDisplay)
      if (needsOptions && (!data.options || data.options.filter(Boolean).length === 0)) {
        ctx.addIssue({ code: 'custom', message: 'At least one option is required', path: ['options'] })
      }
    }
  })

const schemaEditorSchema = z.object({
  fields: z.array(schemaFieldSchema),
})

function sanitizeFieldOptions(field: SchemaField): SchemaField {
  if (!field.options || field.type === 'section') return field

  return {
    ...field,
    options: field.options.map((option) => option.trim()).filter(Boolean),
  }
}

/* ─── Props ─────────────────────────────────────────────── */

export { styles as schemaEditorStyles }

export interface SchemaEditorProps {
  /** Initial flat schema fields to populate the editor */
  initialFields: SchemaField[]
  /** Called with sanitized fields when save is triggered */
  onSave: (fields: SchemaField[]) => void
  /** Whether a save is currently in progress */
  saving: boolean
  /** Content for the cancel button (typically a Link) */
  cancelElement: React.ReactNode
}

/* ─── SchemaEditor ──────────────────────────────────────── */

export function SchemaEditor({ initialFields, onSave, saving, cancelElement }: SchemaEditorProps) {
  const [expandedFields, setExpandedFields] = useState<Set<string | number>>(new Set())
  const isMobile = useIsMobile()
  // Desktop: preview pane visible by default. Mobile: preview opens in a Sheet,
  // closed by default so it doesn't cover the editor on load.
  const [showPreview, setShowPreview] = useState(() => !isMobile)

  const form = useForm<z.infer<typeof schemaEditorSchema>>({
    resolver: zodResolver(schemaEditorSchema),
    defaultValues: { fields: initialFields },
  })
  const {
    control,
    clearErrors,
    getValues,
    handleSubmit,
    setFocus,
    trigger,
    watch,
    formState: { submitCount },
  } = form

  const { fields, append, replace, swap, move } = useFieldArray({
    control,
    name: 'fields',
  })

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const addField = (type: 'field' | 'section') => {
    const newId = Date.now()
    if (type === 'section') {
      append({ id: newId, type: 'section', title: '', description: '' })
      setExpandedFields((prev) => new Set([...prev, newId]))
    } else {
      // If no sections exist yet, auto-prepend one
      const currentFields = getValues('fields')
      const hasSection = currentFields.some((f) => f.type === 'section')
      if (!hasSection) {
        const sectionId = newId - 1
        append({ id: sectionId, type: 'section', title: '', description: '' })
        setExpandedFields((prev) => new Set([...prev, sectionId]))
      }
      append({
        id: newId,
        question: '',
        description: '',
        field_type: 'short_text',
        options: [],
        required: false,
      })
      setExpandedFields((prev) => new Set([...prev, newId]))
    }
  }

  const removeField = (index: number) => {
    const nextFields = getValues('fields').filter((_, fieldIndex) => fieldIndex !== index)
    const removedId = getValues(`fields.${index}.id`)
    clearErrors('fields')
    replace(nextFields)
    setExpandedFields((prev) => {
      const next = new Set(prev)
      next.delete(removedId)
      return next
    })
    if (submitCount > 0) {
      requestAnimationFrame(() => {
        void trigger('fields')
      })
    }
  }

  const watchedFields = watch('fields')

  const canMoveUp = (index: number) => {
    if (index === 0) return false
    const current = watchedFields[index]
    const above = watchedFields[index - 1]
    // Questions can't move above their section header
    if (current?.type !== 'section' && above?.type === 'section') return false
    return true
  }

  const canMoveDown = (index: number) => {
    if (index === fields.length - 1) return false
    const current = watchedFields[index]
    const below = watchedFields[index + 1]
    // Questions can't move below into the next section
    if (current?.type !== 'section' && below?.type === 'section') return false
    return true
  }

  const moveField = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && !canMoveUp(index)) return
    if (direction === 'down' && !canMoveDown(index)) return
    const targetIndex = direction === 'up' ? index - 1 : index + 1
    swap(index, targetIndex)
  }

  const toggleFieldExpanded = (fieldId: string | number) => {
    setExpandedFields((prev) => {
      const next = new Set(prev)
      if (next.has(fieldId)) {
        next.delete(fieldId)
      } else {
        next.add(fieldId)
      }
      return next
    })
  }

  const handleDragStart = (_event: DragStartEvent) => {
    setExpandedFields(new Set())
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = fields.findIndex((f) => f.id === active.id)
    const newIndex = fields.findIndex((f) => f.id === over.id)
    if (oldIndex === -1 || newIndex === -1) return

    // Reject drops that would place a question before the first section
    const draggedField = watchedFields[oldIndex]
    if (draggedField?.type !== 'section') {
      const firstSectionIndex = watchedFields.findIndex((f) => f?.type === 'section')
      if (firstSectionIndex >= 0 && newIndex <= firstSectionIndex) return
    }

    move(oldIndex, newIndex)
  }

  const onValid = (data: z.infer<typeof schemaEditorSchema>) => {
    const sanitizedFields = data.fields.map((field) => sanitizeFieldOptions(field as SchemaField))
    onSave(sanitizedFields)
  }

  const onInvalid = (errors: FieldErrors<z.infer<typeof schemaEditorSchema>>) => {
    const currentFields = getValues('fields')

    // Expand only the fields that have errors so they're visible
    const fieldErrors = errors.fields
    if (Array.isArray(fieldErrors)) {
      const errorFieldIds = new Set<string>()
      fieldErrors.forEach((e, i: number) => {
        if (e != null && i < currentFields.length) {
          errorFieldIds.add(currentFields[i].id.toString())
        }
      })
      setExpandedFields((prev) => new Set([...prev, ...errorFieldIds]))

      // Focus first error field after expansion renders
      const firstErrorIndex = fieldErrors.findIndex((e) => e != null)
      if (firstErrorIndex >= 0 && firstErrorIndex < currentFields.length) {
        const err = fieldErrors[firstErrorIndex]
        const subField = err?.title ? 'title' : err?.question ? 'question' : err?.options ? 'options' : null
        if (subField) {
          requestAnimationFrame(() => {
            setFocus(`fields.${firstErrorIndex}.${subField}`)
          })
        }
      }
    }
    toast.error('Fix validation errors before saving')
  }

  return (
    <Form {...form}>
      <div className="flex flex-col flex-1 min-h-0">
        <div className={styles.splitView}>
          <form
            id="schema-editor-form"
            data-testid="schema-editor"
            onSubmit={handleSubmit(onValid, onInvalid)}
            className={styles.pageContent}
          >
            {/* Fields toolbar */}
            <div className={styles.fieldsHeader}>
              <div className={styles.fieldsLabel}>Form Fields</div>
              <div className={styles.fieldsToolbar}>
                <Button type="button" size="sm" variant="outline" onClick={() => addField('section')}>
                  <Plus className="w-3 h-3 mr-1" />
                  Section
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={() => addField('field')}>
                  <Plus className="w-3 h-3 mr-1" />
                  Field
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  aria-pressed={showPreview}
                  onClick={() => setShowPreview((prev) => !prev)}
                >
                  <PanelRight className="w-3 h-3 mr-1" />
                  {showPreview && !isMobile ? 'Hide preview' : 'Preview'}
                </Button>
              </div>
            </div>

            {fields.length === 0 ? (
              <div className={styles.emptyState}>
                <Layers className={styles.emptyStateIcon} />
                No fields yet. Add a section or field to get started.
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={fields.map((f) => f.id)} strategy={verticalListSortingStrategy}>
                  <div className={styles.fieldsList}>
                    <AnimatePresence initial={false}>
                      {fields.map((field, index) => (
                        <motion.div
                          key={field.id}
                          initial={{ opacity: 0, y: -8, scale: 0.98 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -8, scale: 0.96 }}
                          transition={{ duration: 0.25, ease: 'easeOut' }}
                        >
                          <FieldEditor
                            control={control}
                            index={index}
                            fieldId={field.id}
                            fieldValues={watchedFields[index]}
                            expanded={expandedFields.has(watchedFields[index]?.id)}
                            onToggle={() => toggleFieldExpanded(watchedFields[index]?.id)}
                            onRemove={() => removeField(index)}
                            onMoveUp={() => moveField(index, 'up')}
                            onMoveDown={() => moveField(index, 'down')}
                            disableMoveUp={!canMoveUp(index)}
                            disableMoveDown={!canMoveDown(index)}
                            allFields={watchedFields}
                            styles={styles}
                          />
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </form>

          {!isMobile && showPreview && (
            <div className={styles.previewPane} data-testid="schema-preview">
              <div className={styles.previewHeader}>
                <SectionHeader
                  as="h3"
                  title="Preview"
                  subtitle="How this brief will appear when filled out"
                />
              </div>
              <div className={styles.previewBody}>
                <SchemaPreview fields={watchedFields as SchemaField[]} />
              </div>
            </div>
          )}
        </div>

        <div className={styles.pageFooter}>
          {cancelElement}
          <Button type="submit" form="schema-editor-form" disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save Schema
          </Button>
        </div>
      </div>

      {isMobile && (
        <Sheet open={showPreview} onOpenChange={setShowPreview}>
          <SheetContent side="bottom" className="flex h-[90vh] flex-col" data-testid="schema-preview">
            <SheetHeader className="text-left">
              <SheetTitle>Preview</SheetTitle>
              <SheetDescription>How this brief will appear when filled out</SheetDescription>
            </SheetHeader>
            <div className="-mx-6 mt-2 flex-1 overflow-y-auto px-6">
              <SchemaPreview fields={watchedFields as SchemaField[]} />
            </div>
          </SheetContent>
        </Sheet>
      )}
    </Form>
  )
}
